import { useState } from 'react';
import { toast } from 'sonner';
import { admin, apiErrorMessage, type AdminReportItem } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from '../../i18n';
import { DataTable, Button, StatusBadge, Tabs, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

const STATUSES = ['open', 'requeued', 'resolved', 'dismissed', 'all'];

export default function AdminReportsPage() {
  const t = useAdminT();
  const { t: gt } = useTranslation();
  const [status, setStatus] = useState('open');
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  const q = useApi(() => admin.reports(status, 100), [status, refreshKey]);
  const rows = q.status === 'success' ? q.data : [];

  async function decide(r: AdminReportItem, action: 'resolve' | 'dismiss') {
    setBusyId(r.id);
    try {
      await admin.reportDecision(r.id, { action });
      toast.success(t('reports.resolved'));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column<AdminReportItem>[] = [
    { key: 'reason', header: t('reports.reason'), width: '130px', mono: true, render: (r) => r.reason },
    { key: 'detail', header: t('reports.detail'), render: (r) => r.detail ?? '—' },
    { key: 'status', header: t('col.status'), width: '110px', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'actions', header: t('col.actions'), width: '150px', align: 'right',
      render: (r) => (
        r.status === 'open' || r.status === 'requeued' ? (
          <span className="admin-row-actions">
            <Button size="sm" variant="link" disabled={busyId === r.id} onClick={() => decide(r, 'resolve')}>
              {t('reports.resolve')}
            </Button>
            <Button size="sm" variant="link" disabled={busyId === r.id} onClick={() => decide(r, 'dismiss')}>
              {t('reports.dismiss')}
            </Button>
          </span>
        ) : <span className="admin-muted">—</span>
      ) },
  ];

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('reports.title')}</h1>
        <p className="admin-page__sub">{t('reports.subtitle')}</p>
      </header>

      <div className="admin-toolbar">
        <Tabs items={STATUSES.map((s) => ({ key: s, label: s }))} value={status} onChange={setStatus} />
      </div>

      <DataTable columns={columns} rows={rows} rowKey={(r) => r.id}
        loading={q.status === 'loading'} empty={t('reports.none')} />
    </div>
  );
}
